import React, { useState } from 'react';
import {
  Card,
  Text,
  Button,
  Badge,
  BlockStack,
  InlineStack,
  Select,
  DataTable,
  Pagination,
  EmptyState,
  Box,
  Filters,
  ChoiceList,
  RangeSlider,
} from '@shopify/polaris';
import {
  RefreshIcon,
  ExportIcon,
  DeleteIcon,
} from '@shopify/polaris-icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';

interface AgentLog {
  id: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  category: 'connection' | 'sync' | 'tally' | 'system';
  message: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

interface LogsResponse {
  items: AgentLog[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const levelOptions = [
  { label: 'All Levels', value: '' },
  { label: 'Debug', value: 'debug' },
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warn' },
  { label: 'Error', value: 'error' },
];

const categoryOptions = [
  { label: 'All Categories', value: '' },
  { label: 'Connection', value: 'connection' },
  { label: 'Sync', value: 'sync' },
  { label: 'Tally', value: 'tally' },
  { label: 'System', value: 'system' },
];

const SyncLogsViewer: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [levelFilter, setLevelFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const pageSize = 25;

  // Fetch logs
  const { data: logsData, isLoading, error } = useQuery(
    ['agentLogs', page, levelFilter, categoryFilter],
    () =>
      api.agent.getLogs({
        level: levelFilter || undefined,
        category: categoryFilter || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    {
      refetchInterval: 15000, // Refresh every 15 seconds
      keepPreviousData: true,
    }
  );

  // Fetch log stats
  const { data: statsData } = useQuery(
    'agentLogStats',
    () => api.agent.getLogStats(7),
    {
      refetchInterval: 60000, // Refresh every minute
    }
  );

  // Export logs mutation
  const exportMutation = useMutation(
    () =>
      api.agent.exportLogs({
        level: levelFilter || undefined,
        category: categoryFilter || undefined,
      }),
    {
      onSuccess: (response) => {
        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `agent-logs-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
    }
  );

  // Clear logs mutation
  const clearMutation = useMutation(
    () => api.agent.clearLogs({ olderThanDays: 30 }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agentLogs');
        queryClient.invalidateQueries('agentLogStats');
      },
    }
  );

  const logs: LogsResponse | undefined = logsData?.data;
  const stats = statsData?.data?.stats;

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'error':
        return <Badge tone="critical">Error</Badge>;
      case 'warn':
        return <Badge tone="warning">Warning</Badge>;
      case 'info':
        return <Badge tone="info">Info</Badge>;
      case 'debug':
        return <Badge>Debug</Badge>;
      default:
        return <Badge>{level}</Badge>;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'sync':
        return <Badge tone="success">Sync</Badge>;
      case 'connection':
        return <Badge tone="info">Connection</Badge>;
      case 'tally':
        return <Badge tone="attention">Tally</Badge>;
      case 'system':
        return <Badge>System</Badge>;
      default:
        return <Badge>{category}</Badge>;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const tableRows = logs?.items.map((log) => [
    formatDate(log.createdAt),
    getLevelBadge(log.level),
    getCategoryBadge(log.category),
    <Text as="span" breakWord>
      {log.message}
    </Text>,
  ]) || [];

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Agent Logs</Text>
          <InlineStack gap="200">
            <Button
              icon={RefreshIcon}
              onClick={() => queryClient.invalidateQueries('agentLogs')}
              accessibilityLabel="Refresh logs"
            />
            <Button
              icon={ExportIcon}
              onClick={() => exportMutation.mutate()}
              loading={exportMutation.isLoading}
            >
              Export CSV
            </Button>
            <Button
              icon={DeleteIcon}
              tone="critical"
              onClick={() => {
                if (window.confirm('Delete logs older than 30 days?')) {
                  clearMutation.mutate();
                }
              }}
              loading={clearMutation.isLoading}
            >
              Clear Old Logs
            </Button>
          </InlineStack>
        </InlineStack>

        {/* Stats Summary */}
        {stats && (
          <InlineStack gap="400">
            <Box>
              <BlockStack gap="100">
                <Text as="span" tone="subdued">Errors (7d)</Text>
                <Text as="span" variant="headingMd" tone="critical">
                  {stats.errorCount}
                </Text>
              </BlockStack>
            </Box>
            <Box>
              <BlockStack gap="100">
                <Text as="span" tone="subdued">Warnings (7d)</Text>
                <Text as="span" variant="headingMd" tone="caution">
                  {stats.warnCount}
                </Text>
              </BlockStack>
            </Box>
            <Box>
              <BlockStack gap="100">
                <Text as="span" tone="subdued">Syncs (7d)</Text>
                <Text as="span" variant="headingMd" tone="success">
                  {stats.syncCount}
                </Text>
              </BlockStack>
            </Box>
            <Box>
              <BlockStack gap="100">
                <Text as="span" tone="subdued">Total Logs</Text>
                <Text as="span" variant="headingMd">
                  {stats.totalLogs}
                </Text>
              </BlockStack>
            </Box>
          </InlineStack>
        )}

        {/* Filters */}
        <InlineStack gap="300">
          <Box minWidth="150px">
            <Select
              label="Level"
              labelHidden
              options={levelOptions}
              value={levelFilter}
              onChange={(value) => {
                setLevelFilter(value);
                setPage(1);
              }}
            />
          </Box>
          <Box minWidth="150px">
            <Select
              label="Category"
              labelHidden
              options={categoryOptions}
              value={categoryFilter}
              onChange={(value) => {
                setCategoryFilter(value);
                setPage(1);
              }}
            />
          </Box>
        </InlineStack>

        {/* Logs Table */}
        {isLoading ? (
          <Box padding="400">
            <Text as="p" alignment="center" tone="subdued">
              Loading logs...
            </Text>
          </Box>
        ) : error ? (
          <Box padding="400">
            <Text as="p" alignment="center" tone="critical">
              Failed to load logs. Please try again.
            </Text>
          </Box>
        ) : logs?.items.length === 0 ? (
          <EmptyState
            heading="No logs found"
            image=""
          >
            <p>
              {levelFilter || categoryFilter
                ? 'Try adjusting your filters to see more logs.'
                : 'Logs will appear here once the agent starts syncing.'}
            </p>
          </EmptyState>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text']}
              headings={['Time', 'Level', 'Category', 'Message']}
              rows={tableRows}
              truncate
            />

            {/* Pagination */}
            {logs && logs.totalPages > 1 && (
              <InlineStack align="center">
                <Pagination
                  hasPrevious={page > 1}
                  hasNext={page < logs.totalPages}
                  onPrevious={() => setPage(page - 1)}
                  onNext={() => setPage(page + 1)}
                />
                <Text as="span" tone="subdued">
                  Page {page} of {logs.totalPages} ({logs.total} total)
                </Text>
              </InlineStack>
            )}
          </>
        )}
      </BlockStack>
    </Card>
  );
};

export default SyncLogsViewer;
